import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import './CompanyPage.css';

const CompanyPage = () => {
  return (
    <div className="company-page">
      {/* Background */}
      <div className="company-bg">
        <div className="bg-gradient" />
        <div className="grid-pattern" />
        <div className="glow-orb orb-1" />
        <div className="glow-orb orb-2" />
      </div>

      {/* Hero */}
      <section className="company-hero">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section-label">About Us</span>
            <h1>Emerging Robotics</h1>
            <p className="hero-subtitle">
              A California company headquartered in San Francisco,
              building the future of accessible robotics.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="company-content">
        <div className="section-container">
          <motion.div
            className="company-card"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <h2>Our Mission</h2>
            <p>
              Emerging Robotics builds the robot platform for the physical AI future. 
              We're creating platforms, ecosystems, and complete robots that treat AI agents, 
              replayability, and fleet operations as first-class concerns—not optional add-ons.
            </p>
            <p>
              The future isn't just better hardware. It's systems where decision-making, 
              autonomy, and coordination are designed for AI from the start, without 
              overly complicating things for software-first teams.
            </p>
          </motion.div>

          <motion.div
            className="company-card"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.6 }}
          >
            <h2>What We're Building</h2>
            <div className="products-grid">
              <div className="product-item">
                <h3>Gorai</h3>
                <p>
                  The robotics platform for the AI era. Gorai treats AI-driven execution, 
                  action logs, and replay as platform concerns—not bolt-ons. Build robots 
                  like software. Run them like systems. Go from one robot to fleets without 
                  rewriting your architecture.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="company-card"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            <h2>What We Sell</h2>
            <div className="products-grid">
              <div className="product-item">
                <h3>Robot Kits</h3>
                <p>
                  We sell semi-assembled and unassembled kits for our robot designs.
                  Whether you want a hands-on build experience or a faster path to deployment,
                  we have options to fit your needs and skill level.
                </p>
              </div>
              <div className="product-item">
                <h3>Consulting Services</h3>
                <p>
                  Our team provides consulting services in AI and robotics development.
                  From architecture reviews to full implementation support, we help
                  organizations bring their robotics and automation projects to life.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="company-card"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.6 }}
          >
            <h2>Our Approach</h2>
            <p>
              We're building in the open. Our code is open source, our development is
              transparent, and we actively engage with the robotics community. We believe
              the best tools come from real-world use and feedback.
            </p>
            <p>
              Our team combines decades of enterprise software experience with hands-on
              robotics expertise—from competition robotics to naval systems to silicon
              valley engineering at scale.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Team CTA */}
      <section className="team-cta-section">
        <div className="section-container">
          <motion.div
            className="cta-box"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Meet the Team</h2>
            <p>
              Emerging Robotics was founded by a father-son team with a shared passion
              for robotics and a belief that the tools could be better.
            </p>
            <div className="cta-buttons">
              <Link to="/founders" className="cta-primary">
                Meet the Founders
              </Link>
              <a
                href="https://github.com/emergingrobotics"
                target="_blank"
                rel="noopener noreferrer"
                className="cta-secondary"
              >
                View on GitHub
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Back Link */}
      <div className="section-container">
        <motion.div
          className="back-link"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <Link to="/">← Back to Home</Link>
        </motion.div>
      </div>
    </div>
  );
};

export default CompanyPage;
